import { useTranslation } from "react-i18next";
import { Wand2 } from "lucide-react";
import { deriveEnglish, deriveArabic } from "./deptDictionary";
import { translateText } from "../../api/ai";
import { stripLang } from "../../lib/langInput";
import { DeptBranchManager } from "./DeptBranchManager";
import { localName } from "../../lib/localName";

const IN = "w-full rounded-2xl border border-border/50 bg-background px-4 py-2.5 text-sm shadow-neu-sm outline-none focus:ring-2 focus:ring-primary/40";
const LB = "text-[11px] font-bold uppercase tracking-wider text-muted-foreground";

const RowLabel = ({ text, onDerive, deriveText, tid }) => (
  <div className="mb-1.5 flex items-center justify-between">
    <span className={LB}>{text}</span>
    {onDerive ? (
      <button type="button" onClick={onDerive} data-testid={tid}
              className="flex h-6 items-center gap-1 rounded-lg bg-primary/10 px-2 text-[10px] font-bold text-primary transition hover:bg-primary/20">
        <Wand2 className="h-3 w-3" /> {deriveText}
      </button>
    ) : null}
  </div>
);

// حقول القسم — اشتقاق ثنائي (عربي↔إنجليزي) عبر قاموس ثابت بلا AI.
export const DepartmentFormFields = ({ form, onChange, managers, branches, departments, currentId, isEdit }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const mgrMap = (departments || []).reduce((m, d) => { if (d.manager_id && d.id !== currentId) m[d.manager_id] = localName(d); return m; }, {});
  const set = (k) => (e) => onChange({ ...form, [k]: e.target.value });
  const setL = (k, lang) => (e) => onChange({ ...form, [k]: stripLang(e.target.value, lang) });
  const go = (src, dst, fn) => () => { const v = fn(form[src]); if (v) onChange({ ...form, [dst]: v }); };
  const auto = (src, dst, from) => async () => {
    const text = (form[src] || "").trim();
    if (!text) return;
    const v = await translateText(text, from === "ar" ? "en" : "ar");
    if (v) onChange({ ...form, [dst]: v });
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <RowLabel text={`${t("departments.form.nameAr")} *`} onDerive={go("name_ar", "name_en", deriveEnglish)} deriveText="EN" tid="derive-name-en" />
        <input dir="rtl" className={IN} value={form.name_ar} onChange={setL("name_ar", "ar")} onBlur={auto("name_ar", "name_en", "ar")} placeholder={t("departments.form.nameArPh")} data-testid="dept-name-ar" maxLength={120} />
      </div>
      <div>
        <RowLabel text={`${t("departments.form.nameEn")} *`} onDerive={go("name_en", "name_ar", deriveArabic)} deriveText="AR" tid="derive-name-ar" />
        <input dir="ltr" className={IN} value={form.name_en} onChange={setL("name_en", "en")} onBlur={auto("name_en", "name_ar", "en")} placeholder={t("departments.form.nameEnPh")} data-testid="dept-name-en" maxLength={120} />
      </div>
      <div>
        <RowLabel text={t("departments.form.code")} />
        <input dir="ltr" className={`${IN} font-mono uppercase`} value={form.code || ""} onChange={set("code")} placeholder="HR / SAL / WH" data-testid="dept-code" maxLength={8} />
      </div>
      <DeptBranchManager form={form} onChange={onChange} managers={managers} branches={branches} mgrMap={mgrMap} isEdit={isEdit} />
      <div className="sm:col-span-2">
        <RowLabel text={t("departments.form.descriptionAr")} onDerive={go("description", "description_en", deriveEnglish)} deriveText="EN" tid="derive-desc-en" />
        <textarea dir="rtl" className={`${IN} min-h-[70px] resize-y`} value={form.description || ""} onChange={setL("description", "ar")} onBlur={auto("description", "description_en", "ar")} placeholder={t("departments.form.descriptionPh")} data-testid="dept-description" maxLength={400} />
      </div>
      <div className="sm:col-span-2">
        <RowLabel text={t("departments.form.descriptionEn")} onDerive={go("description_en", "description", deriveArabic)} deriveText="AR" tid="derive-desc-ar" />
        <textarea dir="ltr" className={`${IN} min-h-[70px] resize-y`} value={form.description_en || ""} onChange={setL("description_en", "en")} onBlur={auto("description_en", "description", "en")} placeholder={t("departments.form.descriptionEnPh")} data-testid="dept-description-en" maxLength={400} />
      </div>
    </div>
  );
};
